import { useCallback } from 'react';
import { RedirectType } from '../../types';
import { useAnalyticsClient } from '../analytics/analytics-web-client';
import {
  GoogleLoginRedirectParams,
  redirectToAppleLogin,
  redirectToGoogleLogin,
  redirectToMicrosoftLogin,
  redirectToSlackLogin,
  SocialLoginRedirectParams,
} from '../social-login';

type useGoogleLoginHandlerProps = {
  pageId: string;
  redirectType?: RedirectType;
  redirectParams: GoogleLoginRedirectParams;
};

export const useGoogleLoginHandler = ({
  pageId,
  redirectType,
  redirectParams,
}: useGoogleLoginHandlerProps) => {
  const analyticsClient = useAnalyticsClient();

  return useCallback(() => {
    analyticsClient.buttonClickedEvent(pageId, 'googleLoginButton', {
      redirectType,
    });

    redirectToGoogleLogin(redirectParams);
  }, [analyticsClient, pageId, redirectType, redirectParams]);
};

type useSocialLoginHandlersProps = {
  pageId: string;
  redirectType?: RedirectType;
  redirectParams: SocialLoginRedirectParams;
};

export const useSocialLoginHandlers = ({
  pageId,
  redirectType,
  redirectParams,
}: useSocialLoginHandlersProps) => {
  const analyticsClient = useAnalyticsClient();

  const handleSocialLogin = useCallback(
    (redirectFn: (params: SocialLoginRedirectParams) => void, buttonId: string) => {
      analyticsClient.buttonClickedEvent(pageId, buttonId, {
        redirectType,
      });

      redirectFn(redirectParams);
    },
    [analyticsClient, pageId, redirectType, redirectParams]
  );

  const handleMicrosoftLogin = useCallback(
    () => handleSocialLogin(redirectToMicrosoftLogin, 'microsoftLoginButton'),
    [handleSocialLogin]
  );

  const handleAppleLogin = useCallback(
    () => handleSocialLogin(redirectToAppleLogin, 'appleLoginButton'),
    [handleSocialLogin]
  );

  const handleSlackLogin = useCallback(
    () => handleSocialLogin(redirectToSlackLogin, 'slackLoginButton'),
    [handleSocialLogin]
  );

  return {
    handleMicrosoftLogin,
    handleAppleLogin,
    handleSlackLogin,
  };
};
